import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCog, faPlus, faEdit, faTrash, faFileExcel, faUpload, faTimes
} from "@fortawesome/free-solid-svg-icons";

// 메인 플로팅 버튼 (항상 보이는 버튼)
const MainFloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.isOpen ? '#dc3545' : '#007bff'};
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  z-index: 1001;
  transition: all 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
  
  &:hover {
    background: ${props => props.isOpen ? '#c82333' : '#0056b3'};
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(1.1)' : 'scale(1.1)'};
  }
  
  &:active {
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(0.95)' : 'scale(0.95)'};
  }
`;

// 서브 액션 버튼들
const ActionButton = styled.button`
  position: fixed;
  right: 20px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  transition: all 0.3s ease;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  bottom: ${props => props.isOpen ? `${85 + props.index * 65}px` : '20px'};
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.3)'};
  
  background: ${props => {
    switch(props.actionType) {
      case 'new': return '#28a745';
      case 'edit': return '#ffc107';
      case 'delete': return '#dc3545';
      case 'excel': return '#17a2b8';
      case 'upload': return '#6f42c1';
      case 'settings': return '#6c757d';
      default: return '#007bff';
    }
  }};
  
  &:hover {
    transform: ${props => props.isOpen ? 'scale(1.1)' : 'scale(0.3)'};
    filter: brightness(110%);
  }
  
  &:active {
    transform: ${props => props.isOpen ? 'scale(0.95)' : 'scale(0.3)'};
  }
`;

// 액션 버튼 라벨
const ActionLabel = styled.div`
  position: fixed;
  right: 76px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  bottom: ${props => props.isOpen ? `${93 + props.index * 65}px` : '28px'};
  transition: all 0.3s ease;
  pointer-events: none;
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
    border: 4px solid transparent;
    border-left-color: rgba(0, 0, 0, 0.8);
  }
`;

// 오버레이 제거 - 더 이상 사용하지 않음

const MobileFloatingActions = ({ 
  menuConfig = {},
  selectedRows = [],
  onNewPost,
  onEdit,
  onDelete,
  onExcelDownload,
  onUpload,
  onSettings
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // 사용 가능한 액션들 정의
  const actions = [
    {
      key: 'new',
      icon: faPlus,
      label: '신규 등록',
      color: '#28a745',
      show: menuConfig.use_new,
      action: onNewPost,
      disabled: false
    },
    {
      key: 'edit', 
      icon: faEdit,
      label: '수정',
      color: '#ffc107',
      show: menuConfig.use_modify,
      action: onEdit,
      disabled: selectedRows.length === 0
    },
    {
      key: 'delete',
      icon: faTrash, 
      label: '삭제',
      color: '#dc3545',
      show: menuConfig.use_delete,
      action: onDelete,
      disabled: selectedRows.length === 0
    },
    {
      key: 'excel',
      icon: faFileExcel,
      label: '엑셀 다운로드', 
      color: '#17a2b8',
      show: menuConfig.use_excel,
      action: onExcelDownload,
      disabled: false
    },
    {
      key: 'upload',
      icon: faUpload,
      label: '업로드',
      color: '#6f42c1', 
      show: menuConfig.use_upload,
      action: onUpload,
      disabled: false
    },
    {
      key: 'settings',
      icon: faCog,
      label: '설정',
      color: '#6c757d',
      show: true, // 설정은 항상 표시
      action: onSettings,
      disabled: false
    }
  ].filter(action => action.show && !action.disabled);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action) => {
    if (action.action) {
      action.action();
    }
    setIsOpen(false); // 액션 실행 후 메뉴 닫기
  };

  return (
    <>
      {/* 서브 액션 버튼들 */}
      {actions.map((action, index) => (
        <React.Fragment key={action.key}>
          <ActionButton
            isOpen={isOpen}
            index={index}
            actionType={action.key}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled}
          >
            <FontAwesomeIcon icon={action.icon} />
          </ActionButton>
          
          <ActionLabel isOpen={isOpen} index={index}>
            {action.label}
          </ActionLabel>
        </React.Fragment>
      ))}
      
      {/* 메인 플로팅 버튼 */}
      <MainFloatingButton isOpen={isOpen} onClick={handleToggle}>
        <FontAwesomeIcon icon={isOpen ? faTimes : faPlus} />
      </MainFloatingButton>
    </>
  );
};

export default MobileFloatingActions;